import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useExperiences } from 'core/contexts/experienceV2';
import Layout from 'components/Layout';
import { Section } from 'modules/Pricing/styles';
import arrayMutators from 'final-form-arrays';
import { Form } from 'react-final-form';
import { minutesToTime, PayNow, validateLocationData, validateOptionalString, validateRequiredString } from '@utriper/core';
import { CircularIndeterminate, Tabs } from '@utriper/core-ui';
import { Box, Button, CircularProgress, Grid2, IconButton, Typography } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { PRICE_TYPE } from './styles';
import DetailsForm from './components/DetailsForm';
import MultimediaForm from './components/MultimediaForm';
import RatesPoliciesForm from './components/RatesPoliciesForm';
import LocationForm from './components/LocationForm';
import IncludesForm from './components/IncludesForm';

const TAB_FORMS = {
    DETAILS: 0,
    GALLERY: 1,
    RATES_POLICIES: 2,
    LOCATION: 3,
    INCLUDES: 4,
};

const ERROR_FIELD_TAB = {
    name: TAB_FORMS.DETAILS,
    description1: TAB_FORMS.DETAILS,
    description2: TAB_FORMS.DETAILS,
    moduses: TAB_FORMS.DETAILS,
    duration: TAB_FORMS.DETAILS,
    locationData: TAB_FORMS.LOCATION,
    price: TAB_FORMS.RATES_POLICIES,
    currency: TAB_FORMS.RATES_POLICIES,
    payNow: TAB_FORMS.RATES_POLICIES,
    payNowType: TAB_FORMS.RATES_POLICIES,
    cancelPolicies: TAB_FORMS.RATES_POLICIES,
};

const ExperienceEditForm = ({ tab, form, experienceId }) => {
    switch (tab) {
        case TAB_FORMS.DETAILS:
            return <DetailsForm form={form} />;
        case TAB_FORMS.GALLERY:
            return <MultimediaForm form={form} />;
        case TAB_FORMS.RATES_POLICIES:
            return <RatesPoliciesForm form={form} experienceId={experienceId} />;
        case TAB_FORMS.LOCATION:
            return <LocationForm />;
        case TAB_FORMS.INCLUDES:
            return <IncludesForm form={form} />;
        default:
            return null;
    }
};

const ExperienceEditPage = () => {
    const { id } = useParams();
    const experienceId = parseInt(id);
    const history = useHistory();
    const { t } = useTranslation();
    const translate = (key, options) => t(`experience_edit.${key}`, options);
    const { getExperienceById, experience, loading, update, putExperienceMedia, updating } = useExperiences();

    const [view, setView] = useState(TAB_FORMS.DETAILS);

    const getCustomIncludes = (includes = []) => {
        const response = { includes: [], customIncludes: [] };
        if (!Array.isArray(includes) || includes?.length <= 0) return response;

        for (const { id, includesId, customLabel } of includes) {
            if (!Number.isInteger(includesId) && customLabel) {
                const uniqueId = `custom-${new Date().getTime()}-${Math.random()}`;

                response.customIncludes.push({
                    id: uniqueId,
                    includesId: null,
                    label: customLabel,
                    disabled: false,
                    expInclusionId: id,
                });

                response.includes.push(uniqueId);
                continue;
            }
            response.includes.push(includesId);
        }

        return response;
    };

    const initialValues = useMemo(() => {
        const { days: anticipationDays, hours: anticipationHours, minutes: anticipationMinutes } = minutesToTime(experience?.anticipation);

        const cancelPolicies =
            experience?.cancelPolicies?.length > 0
                ? experience?.cancelPolicies?.map(({ id, percentage, time }) => {
                      const { days, hours } = minutesToTime(time);
                      return {
                          percentage,
                          days,
                          hours,
                          cancelPolicyId: id,
                      };
                  })
                : [];

        const { includes, customIncludes } = getCustomIncludes(experience?.include);
        const { includes: notIncludes, customIncludes: customNotIncludes } = getCustomIncludes(experience?.notInclude);
        const customEnvPolicies = experience?.envPoliciesOther ? [{ id: 5, label: experience.envPoliciesOther }] : [];
        const envPolicies = experience?.envPolicies?.map((envPolicy) => envPolicy?.id) || [];

        return {
            ...experience,
            moduses: experience?.moduses?.map((modus) => ({ id: modus })),
            images: experience?.media?.map(({ id, src, type }) => ({
                id,
                src: type === 3 ? src : `${process.env.REACT_APP_CDN_URL}${src}`,
                videoId: type === 3 ? src : null,
                type,
                isVideo: type === 3 ? true : false,
            })),
            priceType: experience?.priceGroupId && experience?.hasPriceGroup ? PRICE_TYPE.PRICE_GROUP : PRICE_TYPE.FIXED,
            cancelPolicies,
            anticipationDays,
            anticipationHours,
            anticipationMinutes,
            includes,
            notIncludes,
            customIncludes,
            customNotIncludes,
            customEnvPolicies,
            envPolicies,
            experienceInclusions: {
                includes: experience?.include,
                notIncludes: experience?.notInclude,
            },
            experienceCancelPolicies: experience?.cancelPolicies,
            experienceModuses: experience?.moduses,
        };
    }, [experience]);

    useEffect(() => {
        getExperienceById(experienceId, history);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [experienceId]);

    const tabOptions = [
        {
            id: TAB_FORMS.DETAILS,
            label: translate('tabs.details'),
        },
        {
            id: TAB_FORMS.GALLERY,
            label: translate('tabs.gallery'),
        },
        {
            id: TAB_FORMS.RATES_POLICIES,
            label: translate('tabs.rates_policies'),
        },
        {
            id: TAB_FORMS.LOCATION,
            label: translate('tabs.location'),
        },
        {
            id: TAB_FORMS.INCLUDES,
            label: translate('tabs.includes'),
        },
    ];

    const handleChangeView = (view) => setView(view);

    const handleBack = () => history.push(`/experience/${experienceId}`);

    const handleSubmit = async (values) => {
        const result = await update(experienceId, values);

        if (result?.id) {
            toast.success(translate('success_message'), {
                position: 'top-right',
                theme: 'colored',
            });

            const media = Array.isArray(values?.images) ? values.images : [];
            await putExperienceMedia(experienceId, media);

            handleBack();
            return;
        }

        // history.push('/experience');
    };

    const validateForm = (values) => {
        const errors = {};

        // TODO: Move these validations to another file and reuse them in the exp wizard form
        const price = values?.price ? parseFloat(values.price) : null;
        const duration = values?.duration ? parseFloat(values.duration) : null;

        if (!validateRequiredString(values?.name)) {
            errors.name = t('common_form.required');
        }

        if (!validateOptionalString(values?.description1)) {
            errors.description1 = t('common_form.max_length');
        }

        if (!validateOptionalString(values?.description2, 3000)) {
            errors.description2 = t('common_form.max_length');
        }

        if (!values?.locationData || !validateLocationData(values?.locationData)) {
            errors.locationData = t('common_form.required');
        }

        if (values?.allowOpenDateBooking) {
            if (!Number.isFinite(price)) {
                errors.price = t('common_form.required');
            }

            if (!Number.isInteger(duration)) {
                errors.duration = t('common_form.required');
            }

            if (!values?.currency) {
                errors.currency = t('common_form.required');
            }
        }

        if (price && (!Number.isFinite(price) || price < 0)) {
            errors.price = translate('experience_form.errors.invalid_price');
        }

        if (Number.isFinite(price) && !values?.currency) {
            errors.currency = translate('experience_form.errors.missing_currency');
        }

        if (values.currency && !Number.isFinite(price)) {
            errors.price = translate('experience_form.errors.missing_price');
        }

        if (duration && (!Number.isInteger(duration) || duration <= 0)) {
            errors.duration = translate('experience_form.errors.invalid_duration');
        }

        // Validate Pay Now
        if (Number.isFinite(values?.payNow) && !Number.isInteger(values?.payNowType)) {
            errors.payNowType = translate('experience_form.errors.missing_pay_now_type');
        }

        if (PayNow.isTypePercentage(values?.payNowType) && values?.payNow > 100) {
            errors.payNow = translate('experience_form.errors.invalid_percentage');
        }

        if (!Number.isFinite(values?.payNow) && Number.isInteger(values?.payNowType)) {
            errors.payNow = translate('experience_form.errors.missing_pay_now');
        }

        // Validate Policies
        if (values?.refundable && (!values?.cancelPolicies || values?.cancelPolicies?.length <= 0)) {
            errors.cancelPolicies = translate('experience_form.errors.missing_cancel_policies');
        }

        return errors;
    };

    return (
        <Layout>
            {loading ? (
                <CircularIndeterminate centered />
            ) : (
                <Section style={{ overflow: 'visible' }}>
                    {/* Header */}
                    <Grid2 container size={12} direction="column" spacing={2} sx={{ top: 0, position: 'sticky', backgroundColor: '#fff', zIndex: 100 }}>
                        <Grid2 container size={12} spacing={1} alignItems="center" justifyContent="flex-start">
                            <Grid2 sx={{ flex: '0 0 auto' }}>
                                <IconButton onClick={handleBack} disabled={updating}>
                                    <ArrowBack sx={{ color: '#131313' }} />
                                </IconButton>
                            </Grid2>
                            <Grid2 sx={{ flex: 1, minWidth: 0 }}>
                                <Typography fontSize={20} fontWeight={900} sx={{ color: '#131313', width: '100%' }}>
                                    {translate('title', { experienceName: experience?.name })}
                                </Typography>
                            </Grid2>
                        </Grid2>

                        {/* Tabs */}
                        <Grid2 container size={12}>
                            <Tabs
                                value={view}
                                options={tabOptions}
                                onChange={handleChangeView}
                                variant="scrollable"
                                scrollButtons={false}
                                sx={{
                                    '& .MuiTabs-indicator': { backgroundColor: '#131313' },
                                    '& .MuiButtonBase-root': { color: '#131313 !important', textTransform: 'none' },
                                }}
                            />
                        </Grid2>
                    </Grid2>

                    {/* Form */}
                    <Form
                        onSubmit={handleSubmit}
                        mutators={{ ...arrayMutators }}
                        validate={validateForm}
                        initialValues={{ ...initialValues }}
                        render={({ handleSubmit, form, errors, hasValidationErrors }) => {
                            const errorEntries = Object.entries(errors || {})
                                .filter(([key, value]) => value && ERROR_FIELD_TAB[key] !== undefined)
                                .map(([key, value]) => {
                                    const tabId = ERROR_FIELD_TAB[key];
                                    const tabLabel = tabOptions.find((tab) => tab.id === tabId)?.label;
                                    const fieldLabel = translate(`fields.${key}`);
                                    return { key, tabId, tabLabel, fieldLabel, message: value };
                                });

                            return (
                                <form onSubmit={handleSubmit} noValidate style={{ width: '100%' }}>
                                    <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px" mt="24px" mb="120px">
                                        <ExperienceEditForm tab={view} form={form} experienceId={experienceId} />

                                        {/* Buttons */}
                                        <Box
                                            sx={{
                                                px: '32px',
                                                py: '16px',
                                                bottom: 0,
                                                left: 0,
                                                right: 0,
                                                backgroundColor: '#fff',
                                                position: 'fixed',
                                                zIndex: 100,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'flex-end',
                                                gap: '4px',
                                            }}
                                        >
                                            {errorEntries.length > 0 && (
                                                <Box sx={{ maxWidth: 480, maxHeight: 96, overflowY: 'auto', textAlign: 'right' }}>
                                                    <Typography fontSize={12} fontWeight={700} sx={{ color: '#d32f2f' }}>
                                                        {translate('errors_summary.title')}
                                                    </Typography>
                                                    <Box component="ul" sx={{ m: 0, pl: 0, listStylePosition: 'inside' }}>
                                                        {errorEntries.map((err) => (
                                                            <Typography key={err.key} component="li" fontSize={11} sx={{ color: '#d32f2f', cursor: 'pointer' }} onClick={() => setView(err.tabId)}>
                                                                {translate('errors_summary.field', {
                                                                    tab: err.tabLabel,
                                                                    field: err.fieldLabel,
                                                                    message: err.message,
                                                                })}
                                                            </Typography>
                                                        ))}
                                                    </Box>
                                                </Box>
                                            )}
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}>
                                                <Button
                                                    sx={{
                                                        border: '1px solid #13131340',
                                                        color: '#131313',
                                                        textTransform: 'none',
                                                        fontWeight: 700,
                                                        py: '8px',
                                                    }}
                                                    disabled={updating}
                                                    variant="outlined"
                                                    onClick={handleBack}
                                                >
                                                    {t('common.cancel')}
                                                </Button>
                                                <Button
                                                    sx={{
                                                        backgroundColor: 'var(--color-utriper-blue)',
                                                        color: 'white',
                                                        textTransform: 'none',
                                                        fontWeight: 700,
                                                        py: '8px',
                                                        '&.Mui-disabled': {
                                                            backgroundColor: '#13131340',
                                                            color: '#ffffffbf',
                                                        },
                                                    }}
                                                    loading={updating}
                                                    loadingIndicator={
                                                        <Box sx={{ display: 'flex' }}>
                                                            <CircularProgress size={20} sx={{ color: '#131313bf' }} />
                                                        </Box>
                                                    }
                                                    type="submit"
                                                    variant="contained"
                                                    disabled={updating || hasValidationErrors}
                                                    onClick={handleSubmit}
                                                >
                                                    {t('common.save_changes')}
                                                </Button>
                                            </Box>
                                        </Box>
                                    </Grid2>
                                </form>
                            );
                        }}
                    />
                </Section>
            )}
        </Layout>
    );
};

export default ExperienceEditPage;
